import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, Injectable } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HttpClientModule,
  HTTP_INTERCEPTORS,
  HttpRequest,
  HttpInterceptor,
  HttpHandler,
  HttpClientJsonpModule,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgBrazil } from 'ng-brazil';
import { TextMaskModule } from 'angular2-text-mask';
import { NgxSpinnerModule } from 'ngx-spinner';
import { TextInputHighlightModule } from 'angular-text-input-highlight';
import {
  MatNativeDateModule,
  MatPaginatorIntl,
  MAT_DATE_LOCALE,
  DateAdapter,
  NativeDateAdapter,
} from '@angular/material';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SidebarModule } from './sidebar/sidebar.module';
import { FooterModule } from './shared/footer/footer.module';
import { NavbarModule } from './shared/navbar/navbar.module';
import { AdminLayoutComponent } from './layouts/admin/admin-layout.component';
import { AuthLayoutComponent } from './layouts/auth/auth-layout.component';
import { AuthGuard } from './guards/auth.guard';
import { AutenticacaoService } from './services/autenticacao.service';
import { MatPaginatorIntlPt } from './custom/mat-paginator-intl-pt';
import { MomentDateModule } from '@angular/material-moment-adapter';
import { JwtInterceptor } from './interceptors/jwt-interceptor';
import { ErrorInterceptor } from './interceptors/error-interceptor';
import { ResetarSenhaComponent } from './resetar-senha/resetar-senha.component';
import { CustomDateAdapter } from './data-adapter/custom-data-adapter';
import { NgSelectModule } from '@ng-select/ng-select';
import { AppConfigService } from './services/app-config.service';
import { CadastrarContatoComponent } from './contato/cadastrar-contato/cadastrar-contato.component';
import { EsqueceuSenhaComponent } from './esqueceu-senha/esqueceu-senha.component';
import { LoginGIComponent } from './login-gi/login-gi.component';
import { ComumModule } from './comum/comum.module';
import { CadastroComponent } from './cadastro/cadastro.component';
import { TestesComponent } from './testes/testes.component';
import { LightboxModule } from 'ngx-lightbox';
import { MaterialModule } from './material.module';
import { DirectivesModule } from './directives/directives.module';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { ThemeService } from './services/theme.service';
import { OctopusConfigurationService } from './services/octopusConfigurationService';
import { HopperOctopusConfiguration } from './hopper';
import { CacheInterceptor } from './interceptors/cache-interceptor';
import { RastreioModule } from './rastreio/rastreio.module';
import { RetryHttpErrorsInterceptor } from './interceptors/retry-http-errors-interceptor';
import { LogoutComponent } from './logout/logout.component';

registerLocaleData(localePt, 'pt-BR');

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const initializerConfigFn = (appConfig: AppConfigService, themeService: ThemeService) => {
  return async () => {
    return appConfig.loadAppConfig().then(() => {
      return themeService.init();
    });
  };
};

@Injectable()
export class NoCacheHeadersInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler) {
    const excecoes = ['https://viacep.com.br/ws/'];

    if (excecoes.some((p) => req.url.startsWith(p))) {
      return next.handle(req);
    } else {
      const authReq = req.clone({
        setHeaders: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });
      return next.handle(authReq);
    }
  }
}

export class AppDateAdapter extends NativeDateAdapter {
  format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } else {
      return date.toDateString();
    }
  }
}

export let options: Partial<IConfig> | (() => Partial<IConfig>) = {};

@NgModule({
  declarations: [
    AppComponent,
    AdminLayoutComponent,
    AuthLayoutComponent,
    EsqueceuSenhaComponent,
    ResetarSenhaComponent,
    LoginGIComponent,
    LogoutComponent,
    CadastrarContatoComponent,
    CadastroComponent,
    TestesComponent,
  ],
  imports: [
    NgxMaskModule.forRoot(options),
    ComumModule,
    BrowserModule,
    AppRoutingModule,
    CommonModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    MatNativeDateModule,
    SidebarModule,
    NavbarModule,
    FooterModule,
    TextMaskModule,
    NgBrazil,
    NgxSpinnerModule,
    HttpClientModule,
    HttpClientJsonpModule,
    MomentDateModule,
    NgSelectModule,
    LightboxModule,
    TextInputHighlightModule,
    DirectivesModule,
    RastreioModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializerConfigFn,
      multi: true,
      deps: [AppConfigService, ThemeService],
    },

    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RetryHttpErrorsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NoCacheHeadersInterceptor, multi: true },

    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },

    { provide: DateAdapter, useClass: CustomDateAdapter },

    // MicroServices
    { provide: HopperOctopusConfiguration, useClass: OctopusConfigurationService },

    AuthGuard,
    AutenticacaoService,
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlPt },
  ],
  bootstrap: [AppComponent],
  entryComponents: [],
})
export class AppModule {}
